import { getProductDetailsDirect } from '../clients/product_client';
import { logError, logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'kitStockLiveRefresh';

/**
 * Live-refresh of kit `stock` / kit lots after the backend re-syncs from
 * component inventory. Do not invent kit StockItems — always apply GET details.
 *
 * If later `parent_kit_ids` appears on component get_details, prefer that over
 * scanning kit rows already in the product list.
 */

function isKitCategory(product) {
  return Boolean(product && product.category === 'Kit');
}

function productIdOf(product) {
  if (!product || product.id == null) return null;
  return product.id;
}

/**
 * Kit ids already in the in-memory product list (current page). Off-page kits
 * are left for the next list/detail fetch.
 *
 * @param {Array|null|undefined} products
 * @returns {Array<number|string>}
 */
export function collectKitIdsFromProductList(products) {
  const list = Array.isArray(products) ? products : [];
  const ids = [];
  const seen = new Set();
  list.forEach(product => {
    if (!isKitCategory(product)) return;
    const id = productIdOf(product);
    if (id == null) return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    ids.push(id);
  });
  return ids;
}

/**
 * Kit product ids on an order/quote line list that are either marked as kits
 * or already present as kit rows in `products`.
 *
 * @param {Array|null|undefined} orderItems
 * @param {Array|null|undefined} products
 * @returns {Array<number|string>}
 */
export function collectKitIdsFromOrderItems(orderItems, products = []) {
  const listKitIds = new Set(
    collectKitIdsFromProductList(products).map(id => String(id))
  );
  const ids = [];
  const seen = new Set();
  (Array.isArray(orderItems) ? orderItems : []).forEach(item => {
    const product = item?.product || item?.product_detail || null;
    const id = product?.id ?? item?.product_id ?? null;
    if (id == null) return;
    const isKit =
      product?.category === 'Kit' ||
      product?.is_kit === true ||
      listKitIds.has(String(id));
    if (!isKit) return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    ids.push(id);
  });
  return ids;
}

/**
 * Patch one kit row with server get_details. Non-kits and unknown ids are ignored.
 *
 * @param {{ products?: Array, kitDetails?: Object|null }} args
 * @returns {Array}
 */
export function applyKitDetailsToProductList({ products, kitDetails } = {}) {
  const list = Array.isArray(products) ? products : [];
  if (!kitDetails || kitDetails.id == null) return list;
  if (kitDetails.category != null && !isKitCategory(kitDetails)) return list;

  return list.map(product => {
    if (!product || String(product.id) !== String(kitDetails.id)) {
      return product;
    }
    if (product.category && !isKitCategory(product) && !isKitCategory(kitDetails)) {
      return product;
    }
    const next = {
      ...product,
      ...kitDetails,
      id: product.id,
    };
    if (kitDetails.stock !== undefined) next.stock = kitDetails.stock;
    if (kitDetails.effective_stock !== undefined) {
      next.effective_stock = kitDetails.effective_stock;
    }
    if (Array.isArray(kitDetails.items)) {
      next.items = kitDetails.items;
    }
    return next;
  });
}

/**
 * Fetch get_details for each kit id in parallel. Null fetches are failures.
 *
 * @param {string} token
 * @param {Array<number|string>} kitIds
 * @param {{ getDetails?: Function }} [options]
 * @returns {Promise<{ details: Object[], failedIds: Array }>}
 */
export async function refreshKitsByIds(token, kitIds, options = {}) {
  const getDetails =
    typeof options.getDetails === 'function'
      ? options.getDetails
      : getProductDetailsDirect;
  const unique = [];
  const seen = new Set();
  (Array.isArray(kitIds) ? kitIds : []).forEach(id => {
    if (id == null || id === '') return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(id);
  });

  if (!token || unique.length === 0) {
    return { details: [], failedIds: [] };
  }

  logInfo(LOG_CONTEXT, 'refreshKitsByIds start', {
    kitCount: unique.length,
    kitIds: unique,
  });

  const results = await Promise.all(
    unique.map(async id => {
      try {
        const details = await getDetails(token, id);
        if (details && typeof details === 'object') {
          return { id, details, failed: false };
        }
        return { id, details: null, failed: true };
      } catch (err) {
        logError(LOG_CONTEXT, 'refreshKitsByIds get_details failed', {
          kitId: id,
          status: err?.response?.status ?? null,
        });
        return { id, details: null, failed: true };
      }
    })
  );

  const details = [];
  const failedIds = [];
  results.forEach(row => {
    if (row.failed || !row.details) {
      failedIds.push(row.id);
      return;
    }
    details.push(row.details);
    logInfo(LOG_CONTEXT, 'refreshKitsByIds kit applied', {
      kitId: row.details.id ?? row.id,
      stock: row.details.stock,
      effectiveStock: row.details.effective_stock,
      itemCount: Array.isArray(row.details.items)
        ? row.details.items.length
        : null,
    });
  });

  if (failedIds.length) {
    logError(LOG_CONTEXT, 'refreshKitsByIds partial failure', {
      failedIds,
      okCount: details.length,
    });
  }

  return { details, failedIds };
}

/**
 * Re-read kits already in the list (or explicit ids) and patch stock/lots.
 *
 * @param {Object} args
 * @param {string} args.token
 * @param {Array} args.products
 * @param {Array} [args.kitIds]
 * @param {*} [args.componentId] - logged only
 * @param {Function} [args.getDetails]
 * @returns {Promise<{ products: Array, details: Object[], failedIds: Array }>}
 */
export async function refreshParentKitsInProductList({
  token,
  products,
  kitIds,
  componentId = null,
  getDetails,
} = {}) {
  const list = Array.isArray(products) ? products : [];
  const ids =
    Array.isArray(kitIds) && kitIds.length
      ? kitIds
      : collectKitIdsFromProductList(list);

  logInfo(LOG_CONTEXT, 'refreshParentKitsInProductList start', {
    componentId,
    kitIds: ids,
  });

  const { details, failedIds } = await refreshKitsByIds(token, ids, {
    getDetails,
  });

  let next = list;
  details.forEach(kitDetails => {
    next = applyKitDetailsToProductList({ products: next, kitDetails });
  });

  return { products: next, details, failedIds };
}
